import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface ModalState {
  display: boolean
  type: string
  title?: string
  id?: number
  callback: (title: string, id?: number) => void | VoidFunction,
}

interface Props {
  modalState: ModalState
  closeCallback: VoidFunction
  titleList: string[]
}

const SearchConditionsModal: FC<Props> = (props) => {
  const { t } = useTranslation()

  const {
    modalState,
    closeCallback,
    titleList,
  } = props

  const [scrollY, setScrollY] = useState<number>(0)
  const [searchConditionsTitle, setSearchConditionsTitle] = useState<string>("")
  const [errorMessage, setErrorMessage] = useState<string>("")

  const validateInput = (title: string) => {
    setSearchConditionsTitle(title)
    if (
      (modalState.type === "create" && titleList.includes(title))
      || (modalState.type === "edit" && titleList.filter((value) => value !== modalState.title).includes(title))
    ) {
      setErrorMessage(t('search.conditions.error.same_name'))
      return
    }
    setErrorMessage("")
  }

  const buildTitle = () => {
    if (modalState.type === "create") {
      return t('search.conditions.modal.create.title')
    }
    if (modalState.type === "edit") {
      return t('search.conditions.modal.edit.title')
    }
    return ""
  }

  const handleSubmit = () => {
    if (modalState.type === "create") {
      modalState.callback(searchConditionsTitle)
    }
    if (modalState.type === "edit") {
      modalState.callback(searchConditionsTitle, modalState.id)
    }
    closeCallback()
  }

  const closeModal = () => {
    closeCallback()
    setErrorMessage("")
  }

  const buildBody = () => {
    if (modalState.type === "create") {
      return (
        <>
          <p>
            {t('search.conditions.modal.create.text')}
            <br />
            {t('search.conditions.modal.text_2')}
          </p>
          <div className="item">
            <div className="title">
              {t('search.conditions.modal.input_title')}
            </div>
            <div className="detail row_1">
              <input
                type="text"
                placeholder=""
                onChange={(e) => validateInput(e.target.value)}
                value={searchConditionsTitle}
              />
              {
                errorMessage && (
                  <div className="error_field">
                    {errorMessage}
                  </div>
                )
              }
            </div>
          </div>
        </>
      )
    }
    if (modalState.type === "edit") {
      return (
        <>
          <p>
            {t('search.conditions.modal.edit.text')}
            <br />
            {t('search.conditions.modal.text_2')}
          </p>
          <div className="item">
            <div className="title">
              {t('search.conditions.modal.input_title')}
            </div>
            <div className="detail row_1">
              <input
                type="text"
                placeholder=""
                onChange={(e) => validateInput(e.target.value)}
                value={searchConditionsTitle}
              />
              {
                errorMessage && (
                  <div className="error_field">
                    {errorMessage}
                  </div>
                )
              }
            </div>
          </div>
        </>
      )
    }
    return <></>
  }

  const buildButtonText = () => {
    if (modalState.type === "create") {
      return t('search.conditions.modal.create.button')
    }
    if (modalState.type === "edit") {
      return t('search.conditions.modal.edit.button')
    }
    return ""
  }

  useEffect(() => {
    const html = document.querySelector('html')
    if (!html) return
    if (modalState.display) {
      const scroll = window.scrollY
      html.classList.add('modal_open')
      html.style.top = `-${scroll}px`
      setScrollY(scroll)
    } else {
      html.classList.remove('modal_open')
      window.scrollTo(0, scrollY)
      setScrollY(0)
    }
    if (modalState.type === "edit" && modalState.title) {
      setSearchConditionsTitle(modalState.title)
    } else {
      setSearchConditionsTitle("")
    }
  }, [modalState.display])

  return (
    <>
      {
        modalState.display && (
          <div className="modal_wrap">
            <div className={`modal_box ${modalState.display || 'none'}`}>
              <div className="modal_header">
                <div className="title">
                  {
                    buildTitle()
                  }
                </div>
                <button type="button" className="close_modal" onClick={closeModal}>
                  &times;
                </button>
              </div>
              <div className="modal_body">
                {
                  buildBody()
                }
              </div>
              <div className="modal_footer">
                <button type="button" className="button cancel" onClick={closeModal}>
                  {t('form.component.cancel')}
                </button>
                {
                  errorMessage || !searchConditionsTitle ? (
                    <button
                      type="button"
                      className={`button ${modalState.type}`}
                      disabled
                    >
                      {
                        buildButtonText()
                      }
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={`button ${modalState.type}`}
                      onClick={() => handleSubmit()}
                    >
                      {
                        buildButtonText()
                      }
                    </button>
                  )
                }
              </div>
            </div>
            <div className="close_screen gray" role="presentation" onClick={closeModal} />
          </div>
        )
      }
      <div className={`screen ${modalState.display || 'none'}`} />
    </>
  )
}

export default SearchConditionsModal
